import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useWordAddinAuth } from "./WordAddinApp";
import { toast } from "sonner";
import DocumentCanvas from "./DocumentCanvas";
import TaskPane from "./TaskPane";
import officeService from "../../services/officeService";
import timeTrackingService from "../../services/timeTrackingService";

// Declare Office global type
declare const Office: any;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Lexy webapp URL for opening cases
const LEXY_WEBAPP_URL = "https://backend-refactor-32.preview.emergentagent.com";

// Sample document content for web demo mode (empty by default in Office mode)
const SAMPLE_DOCUMENT = "";

const MainApp = () => {
  const { token, user, logout } = useWordAddinAuth();
  const [sessionId, setSessionId] = useState(null);
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [documentContent, setDocumentContent] = useState(SAMPLE_DOCUMENT);
  const [selectedText, setSelectedText] = useState("");
  const [selectionRange, setSelectionRange] = useState(null);
  const [scope, setScope] = useState("selection"); // "selection" or "document"
  const [chatHistory, setChatHistory] = useState([]);
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOfficeMode, setIsOfficeMode] = useState(false);
  const [documentName, setDocumentName] = useState("");
  
  // Language preference (read from localStorage, synced with TaskPane)
  const [language, setLanguage] = useState<'nb' | 'en'>(() => {
    return (localStorage.getItem('lexy_plugin_lang') as 'nb' | 'en') || 'nb';
  });
  
  // Listen for language changes from localStorage (set by TaskPane)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'lexy_plugin_lang' && e.newValue) {
        setLanguage(e.newValue as 'nb' | 'en');
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically in case of same-tab changes
    const checkLanguage = () => {
      const storedLang = localStorage.getItem('lexy_plugin_lang') as 'nb' | 'en';
      if (storedLang && storedLang !== language) {
        setLanguage(storedLang);
      }
    };
    const interval = setInterval(checkLanguage, 1000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [language]);
  
  // Work session state
  const [workSessionStats, setWorkSessionStats] = useState(null);
  const [lastSaveResult, setLastSaveResult] = useState(null);
  
  // Lexy metadata from document (if opened from Lexy)
  const [lexyMetadata, setLexyMetadata] = useState(null);
  
  // Document review suggestions (Saga-like feature)
  const [documentSuggestions, setDocumentSuggestions] = useState([]);

  // Check for Office.js environment
  useEffect(() => {
    let selectionUnsubscribe = null;
    let checkInterval: NodeJS.Timeout | null = null;
    
    // Check if Office is ready
    const checkOffice = async () => {
      // Check for Office.js environment
      const isOffice = typeof window !== 'undefined' && (
        window.officeReady || 
        (typeof Office !== 'undefined' && Office.context)
      );
      
      if (isOffice) {
        console.log('MainApp: Detected Office environment', {
          host: window.officeHost,
          platform: window.officePlatform,
          officeReady: window.officeReady
        });
        setIsOfficeMode(true);
        
        // Clear the interval if we found Office
        if (checkInterval) {
          clearInterval(checkInterval);
          checkInterval = null;
        }
        
        // Get document properties including filename
        try {
          const props = await officeService.getDocumentProperties();
          // Prefer filename, then title
          const docName = props.filename || props.title || '';
          if (docName && docName !== 'Untitled') {
            setDocumentName(docName);
            console.log('MainApp: Set document name from properties:', docName);
          }
        } catch (e) {
          console.log('MainApp: Could not get document properties:', e);
        }
        
        // Check for Lexy metadata in document
        try {
          const metadata = await officeService.getLexyMetadata();
          if (metadata && metadata.documentId) {
            console.log('MainApp: Document has Lexy connection:', metadata);
            setLexyMetadata(metadata);
            
            // Set document name from metadata if available
            if (metadata.documentName) {
              setDocumentName(metadata.documentName);
            }
            
            toast.success("Dokument koblet til Lexy", {
              description: `v${metadata.version} - klar for redigering`
            });
          }
        } catch (e) {
          console.log('MainApp: Could not read Lexy metadata:', e);
        }
        
        // Start listening for selection changes
        selectionUnsubscribe = officeService.onSelectionChange((text) => {
          if (text) {
            setSelectedText(text);
            setScope("selection");
          }
        });
        
        return true; // Office found
      }
      return false; // Office not found yet
    };
    
    // Check immediately
    checkOffice();
    
    // Also check periodically for a few seconds (Office.js might load async)
    let attempts = 0;
    checkInterval = setInterval(() => {
      attempts++;
      checkOffice().then(found => {
        if (found || attempts >= 10) {
          if (checkInterval) {
            clearInterval(checkInterval);
            checkInterval = null;
          }
        }
      });
    }, 500);
    
    // Also listen for onReady callback
    officeService.onReady((isWord) => {
      if (isWord) {
        setIsOfficeMode(true);
      }
    });
    
    return () => {
      if (selectionUnsubscribe) {
        selectionUnsubscribe();
      }
      if (checkInterval) {
        clearInterval(checkInterval);
      }
    };
  }, []);

  // Initialize session and fetch cases
  useEffect(() => {
    const init = async () => {
      try {
        // Create session
        const sessionRes = await axios.post(
          `${API}/word-addin/session`,
          { document_name: documentName },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSessionId(sessionRes.data.session_id);

        // Fetch cases
        const casesRes = await axios.get(`${API}/cases`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCases(casesRes.data || []);
      } catch (error) {
        console.error("Init error:", error);
        toast.error("Kunne ikke initialisere");
      }
    };
    if (token) init();
  }, [token, documentName]);

  // Handle text selection from document
  const handleSelection = useCallback((text, range) => {
    // Only update if we have actual text selected
    // This prevents clearing when clicking in taskpane
    if (text) {
      setSelectedText(text);
      setSelectionRange(range);
      setScope("selection");
    }
    // Don't clear selection when empty - user must explicitly clear it
  }, []);

  // Clear selection explicitly
  const handleClearSelection = useCallback(() => {
    setSelectedText("");
    setSelectionRange(null);
    setScope("document");
  }, []);

  // Handle scope change
  const handleScopeChange = (newScope) => {
    setScope(newScope);
    if (newScope === "document") {
      setSelectedText("");
      setSelectionRange(null);
    }
  };

  // Send chat message (draft or improve)
  const handleSendMessage = async (message, type = "draft") => {
    if (!sessionId) return;

    // For improve, we need selected text
    if (type === "improve" && !selectedText) {
      toast.error("Marker tekst først", { description: "Du må markere tekst i dokumentet for å forbedre den." });
      return;
    }

    setLoading(true);
    const userMessage = {
      id: Date.now(),
      role: "user",
      content: message,
      timestamp: new Date().toISOString()
    };
    setChatHistory((prev) => [...prev, userMessage]);

    try {
      let response;
      // Map language code for backend API
      const apiLanguage = language === 'en' ? 'en' : 'no';
      
      if (type === "improve" && selectedText) {
        response = await axios.post(
          `${API}/word-addin/improve`,
          {
            session_id: sessionId,
            case_id: selectedCase?.id,
            selected_text: selectedText,
            instruction: message,
            scope: scope,
            language: apiLanguage
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const aiMessage = {
          id: Date.now() + 1,
          role: "assistant",
          content: response.data.improved_text,
          rationale: response.data.rationale,
          changes_summary: response.data.changes_summary,
          prompt_id: response.data.prompt_id,
          type: "improve",
          diff: response.data.diff,
          timestamp: new Date().toISOString()
        };
        setChatHistory((prev) => [...prev, aiMessage]);
      } else {
        response = await axios.post(
          `${API}/word-addin/draft`,
          {
            session_id: sessionId,
            case_id: selectedCase?.id,
            prompt: message,
            context: {
              selected_text: scope === "selection" ? selectedText : "",
              scope: scope
            },
            language: apiLanguage
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const aiMessage = {
          id: Date.now() + 1,
          role: "assistant",
          content: response.data.suggestion,
          rationale: response.data.rationale,
          prompt_id: response.data.prompt_id,
          type: "draft",
          diff: response.data.diff,
          timestamp: new Date().toISOString()
        };
        setChatHistory((prev) => [...prev, aiMessage]);
      }
    } catch (error) {
      console.error("Message error:", error);
      toast.error("Kunne ikke sende melding");
      const errorMessage = {
        id: Date.now() + 1,
        role: "assistant",
        content: "Beklager, noe gikk galt. Prøv igjen.",
        error: true,
        timestamp: new Date().toISOString()
      };
      setChatHistory((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  // Apply suggestion to document
  const handleApply = async (message, applyMode) => {
    if (!message.content) return;

    const newContent = message.content;
    const originalText = message.original; // Original text from document suggestion
    let changeDescription = "";
    let success = false;

    // If in Office mode, use Office.js API
    if (isOfficeMode) {
      try {
        // If we have original text (from document review suggestions), use find and replace
        if (originalText && originalText.trim()) {
          if (applyMode === "direct") {
            success = await officeService.findAndReplace(originalText, newContent);
            changeDescription = "Erstattet klausul";
          } else if (applyMode === "tracked") {
            success = await officeService.findAndReplaceWithComment(originalText, newContent, message.rationale);
            changeDescription = "Erstattet med kommentar";
          } else if (applyMode === "comment") {
            // First find and select the original text, then add comment
            const found = await officeService.findAndSelect(originalText);
            if (found) {
              success = await officeService.addComment(`Lexy forslag: ${newContent}`);
            }
            changeDescription = "Kommentar lagt til";
          }
        } else {
          // No original text - insert at current selection
          if (applyMode === "direct") {
            success = await officeService.insertText(newContent, 'replace');
            changeDescription = "Direkte innsetting";
          } else if (applyMode === "tracked") {
            success = await officeService.insertTrackedChange(newContent, selectedText);
            changeDescription = "Endringer sporet";
          } else if (applyMode === "comment") {
            success = await officeService.addComment(`Lexy forslag: ${newContent}`);
            changeDescription = "Kommentar lagt til";
          } else if (applyMode === "tracked_comment") {
            success = await officeService.insertTrackedChange(newContent, selectedText);
            if (success && message.rationale) {
              await officeService.addComment(`Begrunnelse: ${message.rationale}`);
            }
            changeDescription = "Sporet + kommentar";
          }
        }
        
        if (success) {
          setSelectedText("");
          setSelectionRange(null);
        }
      } catch (error) {
        console.error("Office.js apply error:", error);
        toast.error("Kunne ikke sette inn tekst i Word");
        return;
      }
    } else {
      // Fallback: Update simulated document (web demo mode)
      let updatedDocument = documentContent;

      if (applyMode === "direct") {
        if (selectionRange && selectedText) {
          updatedDocument =
            documentContent.substring(0, selectionRange.start) +
            newContent +
            documentContent.substring(selectionRange.end);
          changeDescription = "Direkte innsetting";
        } else {
          updatedDocument = documentContent + "\n\n" + newContent;
          changeDescription = "Direkte innsetting (lagt til)";
        }
      } else if (applyMode === "tracked") {
        if (selectionRange && selectedText) {
          const trackedText = `<del class="tracked-delete">${selectedText}</del><ins class="tracked-insert">${newContent}</ins>`;
          updatedDocument =
            documentContent.substring(0, selectionRange.start) +
            trackedText +
            documentContent.substring(selectionRange.end);
          changeDescription = "Endringer sporet";
        } else {
          updatedDocument = documentContent + `\n\n<ins class="tracked-insert">${newContent}</ins>`;
          changeDescription = "Endringer sporet (lagt til)";
        }
      } else if (applyMode === "comment") {
        if (selectionRange) {
          const commentText = `<span class="comment-highlight" title="Lexy forslag: ${newContent.substring(0, 100)}...">${selectedText || "[Kommentar]"}</span>`;
          updatedDocument =
            documentContent.substring(0, selectionRange.start) +
            commentText +
            documentContent.substring(selectionRange.end);
          changeDescription = "Kommentar";
        }
      } else if (applyMode === "tracked_comment") {
        if (selectionRange && selectedText) {
          const trackedCommentText = `<span class="comment-highlight" title="Begrunnelse: ${message.rationale || ''}"><del class="tracked-delete">${selectedText}</del><ins class="tracked-insert">${newContent}</ins></span>`;
          updatedDocument =
            documentContent.substring(0, selectionRange.start) +
            trackedCommentText +
            documentContent.substring(selectionRange.end);
          changeDescription = "Sporet + kommentar";
        }
      }

      setDocumentContent(updatedDocument);
      setSelectedText("");
      setSelectionRange(null);
      success = true;
    }

    if (!success) return;

    // Log the change
    const newChange = {
      id: Date.now(),
      type: applyMode,
      original: selectedText || "",
      new: newContent.substring(0, 100) + (newContent.length > 100 ? "..." : ""),
      timestamp: new Date().toISOString(),
      description: changeDescription
    };
    setChanges((prev) => [newChange, ...prev]);

    // Log to backend
    if (sessionId && message.prompt_id) {
      try {
        await axios.post(
          `${API}/word-addin/changes`,
          {
            session_id: sessionId,
            case_id: selectedCase?.id,
            prompt_id: message.prompt_id,
            change: {
              type: applyMode,
              original_text: selectedText || "",
              new_text: newContent
            }
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error("Log change error:", error);
      }
    }

    toast.success("Endring utført", { description: changeDescription });
  };

  // Run audit
  const handleAudit = async (playbookId) => {
    if (!sessionId) return null;

    setLoading(true);
    try {
      // Get document text - from Word if available, otherwise from simulated content
      let docText = (documentContent || '').replace(/<[^>]*>/g, ""); // Strip HTML from demo
      
      if (isOfficeMode) {
        docText = await officeService.getDocumentText();
      }
      
      const response = await axios.post(
        `${API}/word-addin/audit`,
        {
          session_id: sessionId,
          case_id: selectedCase?.id,
          document_text: docText,
          playbook_id: playbookId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error("Audit error:", error);
      toast.error("Revisjon feilet");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Link to case
  const handleLinkCase = async (caseId) => {
    const selected = cases.find((c) => c.id === caseId);
    setSelectedCase(selected);

    if (sessionId) {
      try {
        await axios.post(
          `${API}/word-addin/link-to-case`,
          {
            session_id: sessionId,
            case_id: caseId,
            document_name: documentName || "Ukjent dokument"
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Removed toast - indicator shows in UI instead
      } catch (error) {
        console.error("Link case error:", error);
      }
    }
  };

  // Save document to Lexy
  const handleSaveToLexy = async () => {
    if (!selectedCase || !isOfficeMode) {
      toast.error("Velg en sak først");
      return;
    }

    try {
      // Get document from Word
      const docFile = await officeService.getDocumentFile() as { blob: Blob; filename: string } | null;
      if (!docFile) {
        toast.error("Kunne ikke hente dokumentet fra Word");
        return;
      }

      // Upload to Lexy via our backend
      const formData = new FormData();
      formData.append('file', docFile.blob, docFile.filename);
      formData.append('case_id', selectedCase.id);

      const response = await axios.post(
        `${API}/word-addin/upload-document`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        const { message, version, action, filename } = response.data;
        
        // Show appropriate toast based on action
        if (action === "updated") {
          toast.success(message, {
            description: `Samme versjon oppdatert`
          });
        } else if (action === "versioned") {
          toast.success(message, {
            description: `${filename}`
          });
        } else {
          toast.success(message, {
            description: `Lagret til "${selectedCase.title || selectedCase.topic}"`
          });
        }
        
        // Log the upload as a change
        const uploadChange = {
          id: Date.now(),
          type: 'upload',
          original: '',
          new: filename || (docFile as any).filename,
          timestamp: new Date().toISOString(),
          description: action === "updated" 
            ? `Dokument oppdatert (v${version})` 
            : action === "versioned"
            ? `Ny versjon opprettet (v${version})`
            : 'Dokument lastet opp'
        };
        setChanges(prev => [uploadChange, ...prev]);
      }
    } catch (error) {
      console.error("Save to Lexy error:", error);
      toast.error("Kunne ikke lagre dokumentet", {
        description: error.response?.data?.detail || error.message
      });
      throw error;
    }
  };

  // Register time entry to Lexy
  const handleRegisterTime = async (timeEntry) => {
    if (!selectedCase || !sessionId) {
      toast.error("Velg en sak først");
      return;
    }

    try {
      const response = await axios.post(
        `${API}/word-addin/time-entry`,
        {
          session_id: sessionId,
          case_id: selectedCase.id,
          start_time: timeEntry.start_time,
          end_time: timeEntry.end_time,
          duration_seconds: timeEntry.duration_seconds,
          description: `Arbeid i dokument: ${documentName}`
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        toast.success("Tid registrert!", {
          description: `${timeEntry.duration_formatted} registrert på saken`
        });
        
        // Log as a change
        const timeChange = {
          id: Date.now(),
          type: 'time_entry',
          original: '',
          new: timeEntry.duration_formatted,
          timestamp: new Date().toISOString(),
          description: `Tid registrert: ${timeEntry.duration_formatted}`
        };
        setChanges(prev => [timeChange, ...prev]);
      }
    } catch (error) {
      console.error("Register time error:", error);
      toast.error("Kunne ikke registrere tid", {
        description: error.response?.data?.detail || error.message
      });
      throw error;
    }
  };

  // Save complete work session to Lexy (document + comments + changes + time)
  const handleSaveWorkSession = async () => {
    // If document has Lexy metadata, use that for automatic case linking
    const hasLexyConnection = lexyMetadata && lexyMetadata.documentId;
    
    // Determine case ID - from metadata or selected case
    const effectiveCaseId = hasLexyConnection ? lexyMetadata.caseId : selectedCase?.id;
    
    if (!effectiveCaseId && !selectedCase) {
      toast.error("Velg en sak først");
      return;
    }

    try {
      // Collect all data from Word
      toast.info("Samler inn data fra dokumentet...");
      
      const workSessionData = await officeService.getWorkSessionData() as any;
      const timeEntry = timeTrackingService.getTimeEntry();
      
      // Create FormData for document upload
      const formData = new FormData();
      
      if (workSessionData?.document) {
        formData.append('file', workSessionData.document.blob, workSessionData.document.filename);
      }
      
      // Add session data as JSON - include Lexy metadata if available
      formData.append('session_data', JSON.stringify({
        session_id: sessionId,
        case_id: effectiveCaseId,
        document_name: documentName,
        document_text: workSessionData.documentText || '',
        comments: workSessionData.comments || [],
        tracked_changes: workSessionData.trackedChanges || [],
        time_entry: {
          start_time: timeEntry.start_time,
          end_time: timeEntry.end_time,
          duration_seconds: timeEntry.duration_seconds,
          duration_formatted: timeEntry.duration_formatted
        },
        properties: workSessionData.properties || {},
        collected_at: workSessionData.collectedAt,
        // Include Lexy metadata for version tracking
        lexy_metadata: hasLexyConnection ? {
          document_id: lexyMetadata.documentId,
          case_id: lexyMetadata.caseId,
          base_version: lexyMetadata.version,
          tenant_id: lexyMetadata.tenantId
        } : null
      }));

      const response = await axios.post(
        `${API}/word-addin/work-session`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        const { document_version, comments_count, time_registered } = response.data;
        
        // Build the Lexy URL for this case
        const lexyUrl = `${LEXY_WEBAPP_URL}/cases/${selectedCase.id}`;
        
        setLastSaveResult({
          lexyUrl,
          documentVersion: document_version,
          commentsCount: comments_count,
          timeRegistered: time_registered
        });
        
        toast.success("Sendt til Lexy!", {
          description: `Dokument v${document_version} • ${comments_count} kommentarer • ${timeEntry.duration_formatted} tid`
        });
        
        // Log the work session as a change
        const sessionChange = {
          id: Date.now(),
          type: 'work_session',
          original: '',
          new: `v${document_version}`,
          timestamp: new Date().toISOString(),
          description: `Arbeidssesjon sendt til Lexy (${comments_count} kommentarer, ${timeEntry.duration_formatted})`
        };
        setChanges(prev => [sessionChange, ...prev]);
        
        // Reset timer after successful upload
        timeTrackingService.reset();
      }
      
      return response.data;
      
    } catch (error) {
      console.error("Save work session error:", error);
      toast.error("Kunne ikke sende til Lexy", {
        description: error.response?.data?.detail || error.message
      });
      throw error;
    }
  };

  // Open case in Lexy webapp
  const handleOpenInLexy = (url) => {
    if (url) {
      window.open(url, '_blank');
    } else if (selectedCase) {
      window.open(`${LEXY_WEBAPP_URL}/cases/${selectedCase.id}`, '_blank');
    }
  };

  // Handle document review - analyze entire document for suggestions
  const handleReviewDocument = async () => {
    setLoading(true);
    try {
      // Get full document content
      let fullContent = documentContent;
      
      if (isOfficeMode) {
        try {
          fullContent = await officeService.getDocumentText();
        } catch (e) {
          console.error("Failed to get document content:", e);
        }
      }
      
      // Language-specific error messages
      const isEnglish = language === 'en';
      
      if (!fullContent || fullContent.length < 50) {
        toast.error(isEnglish ? "Document is too short to analyze" : "Dokumentet er for kort til å analysere");
        setLoading(false);
        return;
      }

      // Map language code for backend API
      const apiLanguage = language === 'en' ? 'en' : 'no';

      // Call the document review API
      const response = await axios.post(
        `${API}/word-addin/review-document`,
        {
          content: fullContent,
          document_name: documentName,
          session_id: sessionId,
          case_id: selectedCase?.id || lexyMetadata?.caseId,
          language: apiLanguage
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.suggestions && response.data.suggestions.length > 0) {
        setDocumentSuggestions(response.data.suggestions);
        const successMsg = isEnglish 
          ? `${response.data.suggestions.length} improvement suggestions found` 
          : `${response.data.suggestions.length} forbedringsforslag funnet`;
        toast.success(successMsg);
      } else {
        const infoMsg = isEnglish 
          ? "No improvement suggestions found - document looks good!" 
          : "Ingen forbedringsforslag funnet - dokumentet ser bra ut!";
        toast.info(infoMsg);
        setDocumentSuggestions([]);
      }
    } catch (error) {
      console.error("Document review error:", error);
      const errorMsg = language === 'en' ? "Could not analyze document" : "Kunne ikke analysere dokumentet";
      toast.error(errorMsg, {
        description: error.response?.data?.detail || error.message
      });
    } finally {
      setLoading(false);
    }
  };

  // Dismiss a suggestion
  const handleDismissSuggestion = (suggestion, index) => {
    setDocumentSuggestions(prev => prev.filter((_, i) => i !== index));
    toast.info("Forslag avvist");
  };

  // Open section in document (scroll to location)
  const handleOpenSection = async (suggestion) => {
    if (isOfficeMode && suggestion.original) {
      try {
        await officeService.findAndSelect(suggestion.original);
        toast.success("Navigerte til seksjonen i dokumentet");
      } catch (e) {
        console.error("Failed to navigate to section:", e);
      }
    }
  };

  // Publish document to client
  const handlePublishToClient = async () => {
    // Get document ID from Lexy metadata or last save result
    const documentId = lexyMetadata?.documentId || lastSaveResult?.documentId;
    
    if (!documentId) {
      toast.error("Lagre dokumentet først før du publiserer");
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/documents/${documentId}/publish`,
        {
          method: 'portal',
          notify_client: true
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        toast.success(response.data.message || 'Dokumentet er publisert til klient!', {
          description: 'Klienten kan nå se dokumentet i portalen'
        });
      }
    } catch (error) {
      console.error("Publish error:", error);
      toast.error("Kunne ikke publisere dokumentet", {
        description: error.response?.data?.detail || error.message
      });
      throw error;
    }
  };

  // Mark document as final (locks it)
  const handleMarkFinal = async () => {
    // Get document ID from Lexy metadata or last save result
    const documentId = lexyMetadata?.documentId || lastSaveResult?.documentId;
    
    if (!documentId) {
      toast.error("Lagre dokumentet først");
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/documents/${documentId}/mark-final`,
        {
          change_summary: 'Markert som final fra Word Add-in'
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        toast.success('Dokumentet er nå markert som final', {
          description: 'Dokumentet er låst og kan ikke endres'
        });
      }
    } catch (error) {
      console.error("Mark final error:", error);
      toast.error("Kunne ikke låse dokumentet", {
        description: error.response?.data?.detail || error.message
      });
      throw error;
    }
  };

  // Update work session stats periodically
  useEffect(() => {
    const updateStats = async () => {
      if (isOfficeMode) {
        try {
          const comments = await officeService.getComments();
          const changes = await officeService.getTrackedChanges();
          setWorkSessionStats({
            commentsCount: comments.length,
            changesCount: changes.length
          });
        } catch (e) {
          // Ignore errors - might not be in Office
        }
      }
    };
    
    // Update every 10 seconds
    updateStats();
    const interval = setInterval(updateStats, 10000);
    return () => clearInterval(interval);
  }, [isOfficeMode]);

  return (
    <div className={`min-h-screen flex flex-col lg:flex-row ${isOfficeMode ? 'office-mode' : ''}`} data-testid="main-app">
      {/* Document Canvas - only shown in web demo mode */}
      {!isOfficeMode && (
        <div className="flex-1 min-h-[50vh] lg:min-h-screen">
          <DocumentCanvas
            content={documentContent}
            onSelection={handleSelection}
            onClearSelection={handleClearSelection}
            selectedText={selectedText}
            selectionRange={selectionRange}
          />
        </div>
      )}

      {/* Task Pane */}
      <TaskPane
        user={user}
        cases={cases}
        selectedCase={selectedCase}
        onSelectCase={handleLinkCase}
        scope={scope}
        selectedText={selectedText}
        onScopeChange={handleScopeChange}
        chatHistory={chatHistory}
        onSendMessage={handleSendMessage}
        onApply={handleApply}
        onAudit={handleAudit}
        changes={changes}
        loading={loading}
        onLogout={logout}
        token={token}
        isOfficeMode={isOfficeMode}
        documentName={documentName}
        onSaveWorkSession={handleSaveWorkSession}
        onPublishToClient={handlePublishToClient}
        onMarkFinal={handleMarkFinal}
        workSessionStats={workSessionStats}
        lastSaveResult={lastSaveResult}
        onOpenInLexy={handleOpenInLexy}
        lexyMetadata={lexyMetadata}
        onReviewDocument={handleReviewDocument}
        documentSuggestions={documentSuggestions}
        onDismissSuggestion={handleDismissSuggestion}
        onOpenSection={handleOpenSection}
      />
    </div>
  );
};

export default MainApp;
